// Pricing for logged hours that aren't on a budget line yet ("Not In Budget"),
// shared by the Budget tab (row previews, totals, Spent To Date) and the At A
// Glance tab (Spent To Date), so both price the same hours the same way.
//
// What that time actually cost uses each entry's own bill rate. The rate for a
// proposed budget line (and for any entry with no synced rate) comes from a
// fee schedule: the one the phase's BQE budget uses, or — when there's no
// budget yet, or the budget has none set — the default sheet (claims sheet for
// a claims phase, otherwise 2026).
import { useEffect, useState } from "react";
import { getAPICall } from "../../../config/apiCalls";

// BQE represents "no fee schedule set" as an all-zero guid, not null.
export const NO_FEE_SCHEDULE_ID = "00000000-0000-0000-0000-000000000000";

// Claims phases (Tools phase name "Claim Support") are billed off the claims sheet.
export const isClaimsPhase = (phaseName) => /claim/i.test(phaseName || "");

// The default rate sheet for a phase: the claims sheet for a claims phase,
// otherwise the current (2026) sheet — update this when the default sheet changes.
// Falls back to the 2026 sheet if no active claims sheet is found.
export const findDefaultFeeSchedule = (feeSchedules, phaseName) => {
	const active = (feeSchedules || []).filter((f) => f.isActive !== false);
	const claimsSheet = isClaimsPhase(phaseName) ? active.find((f) => /claim/i.test(f.name || "")) : null;
	return claimsSheet || active.find((f) => /2026/.test(f.name || "")) || null;
};

// Exact (resourceId, itemId) rate override from a fee schedule's rate table
// ({ services: [{resourceId,itemId,itemType,priority,rate}], expenses: [...] }).
// Resource-group rows are skipped (no group-membership data to match them against).
export const feeScheduleRateFor = (feeSchedule, kind, resourceId, itemId) => {
	if (!feeSchedule || !resourceId) return null;
	const rows = kind === "service" ? feeSchedule.services : feeSchedule.expenses;
	const forResource = (rows || []).filter((r) => r.resourceId === resourceId);
	if (!forResource.length) return null;
	// A row pointed at the specific activity/expense item beats a group-level
	// default (e.g. BQE's "All Activities" group, itemType 139/140) for this
	// resource — but real fee schedules are often *only* group-level, so that's
	// the fallback, not an edge case. We can't verify which groups a given
	// activity/expense actually belongs to, so any group-type row for this
	// resource is treated as its applicable rate.
	if (itemId) {
		const exact = forResource.find((r) => r.itemId === itemId);
		if (exact) return exact.rate;
	}
	const groupItemType = kind === "service" ? 139 : 140;
	const groupRows = forResource.filter((r) => r.itemType === groupItemType);
	if (groupRows.length) {
		groupRows.sort((a, b) => a.priority - b.priority);
		return groupRows[0].rate;
	}
	return null;
};

// Matches a raw billed-activity text (from a time entry) to a real Activity
// catalog entry — exact name match first, then by the "code" portion before
// BQE's ":group" suffix, since time entries often only carry the code.
export const matchActivity = (activityCatalog, billedActivity) => {
	if (!billedActivity) return null;
	const target = billedActivity.trim().toLowerCase();
	const codeOf = (name) => (name || "").split(":")[0].trim().toLowerCase();
	// Only real activities — never auto-match a time entry to an activity group.
	const activities = (activityCatalog || []).filter((a) => !a.isGroup);
	return activities.find((a) => (a.name || "").trim().toLowerCase() === target)
		|| activities.find((a) => codeOf(a.name) === target)
		|| null;
};

// Every resource with hours logged but no key in `excludeKeys`
// (budgeted resources, plus anything else the caller wants hidden), with the
// activity most often billed under — used to pick a fee-schedule rate.
export const unbudgetedServices = (serviceLineItems, timeEntries, resourceContext, excludeKeys) => {
	const resourceByKey = new Map();
	serviceLineItems.forEach((i) => { if (i.resource) resourceByKey.set(resourceContext.keyFor(i.resource, i.resource_id), { name: i.resource, type: i.resource_type || "" }); });
	timeEntries.forEach((e) => { if (e.employee) resourceByKey.set(resourceContext.keyFor(e.employee, e.resource_id), resourceByKey.get(resourceContext.keyFor(e.employee, e.resource_id)) || { name: e.employee, type: "" }); });

	const activityTallyByKey = new Map(); // key -> Map<activityText, count>
	timeEntries.filter((e) => e.activity_description).forEach((e) => {
		const key = resourceContext.keyFor(e.employee, e.resource_id);
		if (!key) return;
		const tally = activityTallyByKey.get(key) || new Map();
		tally.set(e.activity_description, (tally.get(e.activity_description) || 0) + 1);
		activityTallyByKey.set(key, tally);
	});
	const topActivityByKey = new Map();
	activityTallyByKey.forEach((tally, key) => {
		let best = "";
		let bestCount = 0;
		tally.forEach((count, text) => { if (count > bestCount) { best = text; bestCount = count; } });
		if (best) topActivityByKey.set(key, best);
	});

	return [...resourceContext.hoursByKey.entries()]
		.filter(([key, hours]) => hours > 0 && !excludeKeys.has(key))
		.map(([key, hours]) => ({
			key,
			hours,
			// Spent $ split: entries with a synced bill rate vs. hours still unrated
			ratedAmount: resourceContext.ratedAmountByKey.get(key) || 0,
			unratedHours: resourceContext.unratedHoursByKey.get(key) || 0,
			resource: resourceByKey.get(key) || null,
			billedActivity: topActivityByKey.get(key) || "",
		}))
		.sort((a, b) => b.hours - a.hours);
};

// Resolves the rate for one `unbudgetedServices` row: the fee schedule's rate
// for that resource/activity first, then the Activity catalog's own rate, then
// any budget line already using this resource. Time entries are always logged
// by a real person, so resource groups are never a match.
export const priceUnbudgetedService = (u, { feeSchedule, activityCatalog, resourceCatalog, serviceLineItems, keyFor }) => {
	const individualResources = (resourceCatalog || []).filter((r) => !r.isGroup);
	const keyResourceId = u.key.startsWith("id:") ? u.key.slice(3) : null;
	const catalogMatch = (keyResourceId && individualResources.find((r) => r.id === keyResourceId))
		|| individualResources.find((r) => r.name === u.resource?.name);
	const activityMatch = matchActivity(activityCatalog, u.billedActivity);
	const resolvedResourceId = catalogMatch?.id || keyResourceId || "";
	const budgetLineRate = serviceLineItems.find((i) => i.billRate != null && keyFor(i.resource, i.resource_id) === u.key)?.billRate ?? null;
	const previewRate = feeScheduleRateFor(feeSchedule, "service", resolvedResourceId, activityMatch?.id) ?? activityMatch?.billRate ?? budgetLineRate ?? null;
	const previewAmount = previewRate != null ? previewRate * (u.hours || 0) : null;
	// What this time actually cost: rated entries at their own bill rate (as
	// BQE prices them), unrated hours at the preview rate when there is one.
	const unratedHours = u.unratedHours;
	const unpricedHours = previewRate == null ? unratedHours : 0;
	const ratedAmount = u.ratedAmount || 0;
	const spentAmount = unpricedHours > 0 && ratedAmount === 0
		? null
		: ratedAmount + (previewRate != null ? previewRate * unratedHours : 0);
	return { ...u, catalogMatch, activityMatch, resolvedResourceId, previewRate, previewAmount, spentAmount, unpricedHours };
};

// Loads what priceUnbudgetedService needs, for pages that don't already have
// it (the Budget tab fetches these itself): the BQE activity/resource catalogs
// and the fee schedule's rate table — the budget's own when `bqeBudgetId` has
// one set, otherwise the phase's default sheet. Does nothing until `enabled`.
export const useUnbudgetedPricing = (instance, account, bqeBudgetId, enabled, phaseName) => {
	// `loadedFor` is the bqeBudgetId the sources were loaded for, so the render
	// right after `enabled` flips on (before the effect runs) already reads as
	// loading instead of briefly pricing with nothing.
	const [sources, setSources] = useState({ loadedFor: undefined, feeSchedule: null, activityCatalog: [], resourceCatalog: [] });

	useEffect(() => {
		if (!enabled) return;
		let cancelled = false;

		const budgetFeeScheduleId = bqeBudgetId
			? getAPICall(instance, account, `/api/bqe/budget/${bqeBudgetId}`)
				.then((res) => (res.data?.feeScheduleId && res.data.feeScheduleId !== NO_FEE_SCHEDULE_ID ? res.data.feeScheduleId : null))
				.catch(() => null)
			: Promise.resolve(null);

		Promise.allSettled([
			getAPICall(instance, account, "/api/bqe/activity/all"),
			getAPICall(instance, account, "/api/bqe/resource/all"),
			getAPICall(instance, account, "/api/bqe/feeschedule/all"),
			budgetFeeScheduleId,
		]).then(async ([activityRes, resourceRes, feeScheduleListRes, budgetFsRes]) => {
			if (cancelled) return;
			const feeScheduleId = budgetFsRes.value
				|| (feeScheduleListRes.status === "fulfilled" ? findDefaultFeeSchedule(feeScheduleListRes.value.data, phaseName)?.id : null);
			const feeSchedule = feeScheduleId
				? await getAPICall(instance, account, `/api/bqe/feeschedule/${feeScheduleId}`).then((res) => res.data).catch(() => null)
				: null;
			if (cancelled) return;
			setSources({
				loadedFor: bqeBudgetId,
				feeSchedule,
				activityCatalog: activityRes.status === "fulfilled" ? activityRes.value.data || [] : [],
				resourceCatalog: resourceRes.status === "fulfilled" ? resourceRes.value.data || [] : [],
			});
		});

		return () => { cancelled = true; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [bqeBudgetId, enabled, phaseName]);

	return { ...sources, loading: enabled && sources.loadedFor !== bqeBudgetId };
};
